// Shared SF2 chorus bus, from native Web Audio nodes — no worklet needed.
//
// SF2's `chorusEffectsSend` (generator 15), like the reverb send, is only a level: the
// spec mandates no algorithm. This is the classic Sound Canvas-style stereo chorus the
// GS-era banks were voiced against: one modulated delay per side, LFOs in antiphase so
// the two sides detune in opposite directions, light feedback. Built synchronously (no
// addModule), so unlike the reverb bus it exists the moment `bind` runs.
//
// Defaults approximate the SC-88 "Chorus 3" macro — the GS power-on preset.

clamp := (v: number, lo: number, hi: number) -> Math.min Math.max(v, lo), hi

export type ChorusNode = {
  input: GainNode
  output: GainNode
  setParams: (p: {rateHz?: number, depthMs?: number, delayMs?: number, feedback?: number, level?: number}) => void
  dispose: () => void
}

export createChorusNode := (context: BaseAudioContext): ChorusNode ->
  input := context.createGain()
  output := context.createGain()

  // Modulated delay per side. Max delay sized for delayMs + depthMs headroom.
  delayL := context.createDelay(0.1)
  delayR := context.createDelay(0.1)

  lfo := context.createOscillator()
  lfo.type = "sine"
  depthL := context.createGain()
  depthR := context.createGain()
  lfo.connect depthL
  lfo.connect depthR
  depthL.connect delayL.delayTime
  depthR.connect delayR.delayTime
  lfo.start 0

  fbL := context.createGain()
  fbR := context.createGain()
  delayL.connect fbL
  fbL.connect delayL
  delayR.connect fbR
  fbR.connect delayR

  merger := context.createChannelMerger 2
  input.connect delayL
  input.connect delayR
  delayL.connect merger, 0, 0
  delayR.connect merger, 0, 1
  merger.connect output

  setParams := ({rateHz, depthMs, delayMs, feedback, level}: {rateHz?: number, depthMs?: number, delayMs?: number, feedback?: number, level?: number}) ->
    t := context.currentTime
    if rateHz?
      lfo.frequency.setValueAtTime clamp(rateHz, 0.05, 10), t
    if delayMs?
      base := clamp(delayMs, 0.5, 50) / 1000
      delayL.delayTime.setValueAtTime base, t
      delayR.delayTime.setValueAtTime base, t
    if depthMs?
      d := clamp(depthMs, 0, 20) / 1000 / 2
      // Antiphase: one side stretches while the other compresses — that opposite detune
      // is what reads as width rather than as vibrato.
      depthL.gain.setValueAtTime d, t
      depthR.gain.setValueAtTime -d, t
    if feedback?
      fb := clamp feedback, 0, 0.9
      fbL.gain.setValueAtTime fb, t
      fbR.gain.setValueAtTime fb, t
    if level?
      output.gain.setValueAtTime clamp(level, 0, 2), t

  // SC-88 Chorus 3-ish power-on state. Depth stays small: delay modulation IS pitch
  // modulation (6ms at this rate measured ±19 cents of wobble in the wet — heard as
  // "the pitch is off" rather than as width; 2.5ms keeps it under ±8).
  setParams { rateHz: 0.6, depthMs: 2.5, delayMs: 16, feedback: 0.06, level: 1 }

  input: input
  output: output
  setParams: setParams
  dispose: ->
    try lfo.stop()
    input.disconnect()
    output.disconnect()
